import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, HStack, VStack, Text, Badge, Button, Alert, AlertIcon,
  Input, Select, Table, Thead, Tbody, Tr, Th, Td, Divider,
  Stat, StatLabel, StatNumber, StatHelpText, Stack, Tag, TagLabel,
  SimpleGrid, Collapse, Skeleton,
} from "@chakra-ui/react";
import {
  fetchAuditLogs, fetchAuditLogsRetention, fetchImpersonationAudit,
  fetchAuditLogsStatus, fetchAuditLogsSummary,
} from "../../services/rlsAudit";
import {
  readCache, writeCache, formatAge,
  AUDIT_LOGS_KEY, AUDIT_RETENTION_KEY, IMPERSONATION_AUDIT_KEY,
} from "../../utils/resultCache";

export function cleanText(value, fallback = "-") {
  if (value === null || value === undefined) return fallback;
  let text = String(value);
  if (!text.trim()) return fallback;
  text = text
    .replace(/\u00e2\u20ac[\u201c\u201d\u0090-\u009f]/g, "\u2014")
    .replace(/\u00e2\u20ac\u00a6/g, "...")
    .replace(/\u00e2\u20ac\u2122/g, "'");
  // Repair a common UTF-8-as-Windows-1252 round trip only if a strict decode
  // succeeds; genuine accented names then remain untouched.
  if ([...text].every((char) => char.charCodeAt(0) <= 0xff)) {
    try { text = decodeURIComponent(escape(text)); } catch { /* genuine Latin-1 */ }
  }
  // eslint-disable-next-line no-control-regex
  return text.replace(/[\u0000-\u001f\u007f-\u009f\ufffd]/g, " ").replace(/\s+/g, " ").trim() || fallback;
}

function displayText(value, fallback) {
  return cleanText(value, fallback)
    .replace(/\u2014/g, " - ").replace(/\u2013/g, "-")
    .replace(/\u2026/g, "...").replace(/\u00b7/g, "-")
    .replace(/\u00a7/g, "section ").replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"').replace(/\s+/g, " ").trim();
}

const ACTION_LABELS = {
  CREATE: "Created", READ: "Viewed", UPDATE: "Edited", DELETE: "Deleted",
  EXPORT: "Exported", PRINT: "Printed", DOWNLOAD: "Downloaded", FAX_SEND: "Fax sent",
  LOGIN: "Signed in", LOGIN_FAILED: "Sign-in failed", LOGOUT: "Signed out",
  ACCESS_DENIED: "Access denied", PASSWORD_CHANGE: "Password changed",
  ADMIN_ACTION: "Admin action", ROLE_CHANGE: "Access changed", USER_CREATE: "User created",
  USER_UPDATE: "User updated", USER_DISABLE: "User disabled", CONFIG_CHANGE: "Config changed",
  IMPERSONATION: "View-as started", IMPERSONATION_END: "View-as ended",
};

function inferEventCategory(action) {
  if (["EXPORT", "PRINT", "DOWNLOAD", "FAX_SEND"].includes(action)) return "DATA_OUT";
  if (["LOGIN", "LOGIN_FAILED", "LOGOUT", "PASSWORD_CHANGE"].includes(action)) return "AUTH";
  if (action === "ACCESS_DENIED") return "DENIED";
  if (action === "IMPERSONATION" || action === "IMPERSONATION_END") return "IMPERSONATION";
  if (["ADMIN_ACTION", "ROLE_CHANGE", "USER_CREATE", "USER_UPDATE", "USER_DISABLE", "CONFIG_CHANGE"].includes(action)) return "ADMIN";
  return "PHI";
}

const ACTION_OPTIONS = [
  "",
  "CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "PRINT", "DOWNLOAD", "FAX_SEND",
  "LOGIN", "LOGIN_FAILED", "LOGOUT", "ACCESS_DENIED", "PASSWORD_CHANGE",
  "ADMIN_ACTION", "ROLE_CHANGE", "USER_CREATE", "USER_UPDATE", "USER_DISABLE", "CONFIG_CHANGE",
  "IMPERSONATION", "IMPERSONATION_END",
];

const ACTION_COLORS = {
  CREATE: "blue",
  READ: "gray",
  UPDATE: "yellow",
  DELETE: "red",
  EXPORT: "purple",
  PRINT: "purple",
  DOWNLOAD: "purple",
  FAX_SEND: "purple",
  LOGIN: "green",
  LOGIN_FAILED: "orange",
  LOGOUT: "gray",
  ACCESS_DENIED: "red",
  PASSWORD_CHANGE: "cyan",
  IMPERSONATION: "pink",
  IMPERSONATION_END: "pink",
  ADMIN_ACTION: "orange",
  ROLE_CHANGE: "orange",
  USER_CREATE: "orange",
  USER_UPDATE: "orange",
  USER_DISABLE: "red",
  CONFIG_CHANGE: "orange",
};

function ActionBadge({ action }) {
  return (
    <Badge colorScheme={ACTION_COLORS[action] || "gray"} fontSize="xs">
      {ACTION_LABELS[action] || displayText(action, "Activity")}
    </Badge>
  );
}

function fmtTs(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return displayText(value);
  return `${d.toISOString().replace("T", " ").slice(0, 19)} UTC`;
}

function formatDuration(startIso, endIso) {
  if (!startIso) return "-";
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  if (Number.isNaN(start) || Number.isNaN(end)) return "-";
  const seconds = Math.max(0, Math.round((end - start) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

function RetentionSummary({ status, age }) {
  if (!status) {
    return <Text fontSize="sm" color="gray.600">-</Text>;
  }
  const past = status.rows_past_retention ?? 0;
  return (
    <HStack spacing={6} align="flex-start" wrap="wrap">
      <Stat minW="140px">
        <StatLabel fontSize="sm">Retention</StatLabel>
        <StatNumber fontSize="lg">{status.retention_years ?? 7} years</StatNumber>
        <StatHelpText fontSize="xs">HIPAA section 164.316(b)(2)(i)</StatHelpText>
      </Stat>
      <Stat minW="140px">
        <StatLabel fontSize="sm">Total audit rows</StatLabel>
        <StatNumber fontSize="lg">{status.total_rows?.toLocaleString?.() ?? status.total_rows ?? 0}</StatNumber>
        <StatHelpText fontSize="xs">{status.oldest_row ? `oldest ${fmtTs(status.oldest_row)}` : "no rows yet"}</StatHelpText>
      </Stat>
      <Stat minW="160px">
        <StatLabel fontSize="sm">Past retention horizon</StatLabel>
        <StatNumber fontSize="lg" color={past > 0 ? "orange.600" : "green.600"}>
          {past.toLocaleString?.() ?? past}
        </StatNumber>
        <StatHelpText fontSize="xs">{past > 0 ? "ready to archive" : "under horizon"}</StatHelpText>
      </Stat>
      {age && (
        <Stat minW="140px">
          <StatLabel fontSize="sm">Snapshot</StatLabel>
          <StatNumber fontSize="lg">{age}</StatNumber>
          <StatHelpText fontSize="xs">refresh to update</StatHelpText>
        </Stat>
      )}
    </HStack>
  );
}

function ActiveImpersonations({ rows }) {
  if (!rows || !rows.length) return null;
  const active = rows.filter((r) => !r.ended_at);
  if (!active.length) return null;
  return (
    <Alert status="warning" borderRadius="md">
      <AlertIcon />
      <Box>
        <Text fontWeight="bold">{active.length} active view-as session{active.length === 1 ? "" : "s"}</Text>
        <Text fontSize="sm">
          Click "End impersonation" in AdminPage to close them, or they will
          expire when the JWT TTL elapses.
        </Text>
      </Box>
    </Alert>
  );
}

function ImpersonationTable({ rows, loading, lastRun }) {
  if (loading && !rows.length) {
    return <VStack align="stretch" spacing={2}>
      {[0, 1, 2].map((key) => <Skeleton key={key} height="42px" borderRadius="md" />)}
    </VStack>;
  }
  if (!rows.length) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">No impersonation sessions recorded yet.</Text>
      </Alert>
    );
  }
  return (
    <Table size="sm" variant="simple">
      <Thead>
        <Tr>
          <Th>Started</Th>
          <Th>Duration</Th>
          <Th>Admin</Th>
          <Th>Viewing as</Th>
          <Th>Schema</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((r) => (
          <Tr key={r.id}>
            <Td>{fmtTs(r.started_at)}</Td>
            <Td>
              {formatDuration(r.started_at, r.ended_at)}
              {!r.ended_at && <Badge ml={2} colorScheme="orange" fontSize="xs">active</Badge>}
            </Td>
            <Td>{displayText(r.admin_email || r.admin_id)}</Td>
            <Td>{displayText(r.target_email || r.target_id)}</Td>
            <Td>{displayText(r.target_schema)}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

function AuditTable({ rows, loading }) {
  if (loading && !rows.length) {
    return <VStack align="stretch" spacing={2}>
      {[0, 1, 2, 3].map((key) => <Skeleton key={key} height="58px" borderRadius="md" />)}
    </VStack>;
  }
  if (!rows.length) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="bold">No matching audit log rows.</Text>
          <Text fontSize="sm">
            No events match the current filters. Clear a filter to broaden the results. If the timeline is empty, check the recording status and migration notices above.
          </Text>
        </Box>
      </Alert>
    );
  }
  return (
    <VStack align="stretch" spacing={2}>
      {rows.map((r) => <AuditEventCard key={r.id} row={r} />)}
    </VStack>
  );
}

function AuditEventCard({ row }) {
  const [open, setOpen] = useState(false);
  const actionKey = row.metadata?.requested_action_type || row.action_type;
  const action = ACTION_LABELS[actionKey] || displayText(actionKey, "Activity");
  const category = displayText(row.event_category || row.metadata?.requested_event_category || inferEventCategory(actionKey), "PHI");
  const resource = displayText(row.resource_table || row.resource_type, "resource");
  const actor = displayText(row.actor_email, "Anonymous");
  const success = row.success !== false;
  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden">
      <Button variant="ghost" w="100%" h="auto" p={3} justifyContent="flex-start" textAlign="left" onClick={() => setOpen((value) => !value)}>
        <Stack direction={{ base: "column", md: "row" }} align={{ base: "flex-start", md: "center" }} spacing={2} w="100%">
          <HStack minW={{ md: "190px" }}>
            <Badge colorScheme={success ? "green" : "red"}>{success ? "OK" : "FAILED"}</Badge>
            <ActionBadge action={actionKey} />
          </HStack>
          <Box flex="1" minW={0}>
            <Text fontSize="sm" noOfLines={1}>{actor} {action.toLowerCase()} {resource}</Text>
            <Text fontSize="xs" color="gray.600" noOfLines={1}>
              {displayText([row.actor_role, row.patient_id && `patient ${row.patient_id}`, row.schema_name, row.ip_address].filter(Boolean).join(" | "), "No additional context")}
            </Text>
          </Box>
          <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">{fmtTs(row.occurred_at)}</Text>
        </Stack>
      </Button>
      <Collapse in={open} animateOpacity>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} p={3} bg="gray.50" _dark={{ bg: "whiteAlpha.100" }}>
          {[
            ["Actor", `${actor} | ${displayText(row.actor_user_id)} | ${displayText(row.actor_role)}`],
            ["Target account", `${displayText(row.target_email)} | ${displayText(row.target_user_id)}`],
            ["Action / category", `${action} | ${category}`],
            ["Resource / record", `${resource} | ${displayText(row.resource_id)}`],
            ["Exported records", row.metadata?.record_count],
            ["Patient", row.patient_id], ["Source IP", row.ip_address], ["Session", row.session_id],
            ["Schema / organization", `${displayText(row.schema_name)} | ${displayText(row.org_name)}`],
            ["Outcome", `${success ? "success" : "failure"} | ${displayText(row.outcome_reason || row.error_message)}`],
            ["Request", `${displayText(row.request_method)} ${displayText(row.request_path)}`],
            ["Request id", row.request_id],
          ].map(([label, value]) => (
            <Box key={label} minW={0}>
              <Text fontSize="xs" color="gray.500" textTransform="uppercase">{label}</Text>
              <Text fontSize="sm" wordBreak="break-word">{displayText(value)}</Text>
            </Box>
          ))}
        </SimpleGrid>
      </Collapse>
    </Box>
  );
}

export default function AuditLogsPanel() {
  const [filters, setFilters] = useState({
    event_category: "",
    action_type: "",
    resource_type: "",
    actor_email: "",
    patient_id: "",
    search: "",
    success: "",
  });
  const [rows, setRows] = useState(() => readCache(AUDIT_LOGS_KEY)?.data?.rows ?? []);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [loadWarnings, setLoadWarnings] = useState([]);
  const [lastRun, setLastRun] = useState(readCache(AUDIT_LOGS_KEY)?.savedAt ?? null);
  const [writerStatus, setWriterStatus] = useState(null);
  const [summary, setSummary] = useState(null);

  const [retention, setRetention] = useState(readCache(AUDIT_RETENTION_KEY)?.data ?? null);
  const [retentionAge, setRetentionAge] = useState(readCache(AUDIT_RETENTION_KEY)?.savedAt ?? null);
  const [retentionLoading, setRetentionLoading] = useState(false);

  const [impRows, setImpRows] = useState(readCache(IMPERSONATION_AUDIT_KEY)?.data?.rows ?? []);
  const [impLoading, setImpLoading] = useState(false);
  const [impLastRun, setImpLastRun] = useState(readCache(IMPERSONATION_AUDIT_KEY)?.savedAt ?? null);
  const initialAuditLoad = useRef(false);

  const runStatus = useCallback(async () => {
    try {
      setWriterStatus(await fetchAuditLogsStatus());
    } catch {
      setWriterStatus({ enabled: false, summary: "Could not reach the audit logging status endpoint." });
    }
  }, []);

  const runSummary = useCallback(async () => {
    try { setSummary(await fetchAuditLogsSummary()); }
    catch { setSummary(null); }
  }, []);

  const runAudit = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const body = {};
      if (filters.event_category) body.event_category = filters.event_category;
      if (filters.action_type) body.action_type = filters.action_type;
      if (filters.resource_type) body.resource_type = filters.resource_type;
      if (filters.actor_email) body.actor_email = filters.actor_email;
      if (filters.patient_id) body.patient_id = filters.patient_id;
      if (filters.search) body.search = filters.search;
      if (filters.success === "true") body.success = true;
      else if (filters.success === "false") body.success = false;
      const fresh = await fetchAuditLogs(body);
      const payload = { rows: fresh.rows || [], limit: fresh.limit, offset: fresh.offset };
      setLoadWarnings(fresh.warnings || []);
      setRows(payload.rows);
      setLastRun(new Date().toISOString());
      writeCache(AUDIT_LOGS_KEY, payload);
    } catch (err) {
      setLoadError(err?.message || "Could not load audit events.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const runRetention = useCallback(async () => {
    setRetentionLoading(true);
    try {
      const fresh = await fetchAuditLogsRetention();
      setRetention(fresh);
      setRetentionAge(new Date().toISOString());
      writeCache(AUDIT_RETENTION_KEY, fresh);
    } catch (_err) {
      // The modal owns the toast; cached status remains visible.
    } finally {
      setRetentionLoading(false);
    }
  }, []);

  const runImpersonations = useCallback(async () => {
    setImpLoading(true);
    try {
      const fresh = await fetchImpersonationAudit({});
      const payload = { rows: fresh.rows || [] };
      setImpRows(payload.rows);
      setImpLastRun(new Date().toISOString());
      writeCache(IMPERSONATION_AUDIT_KEY, payload);
    } finally {
      setImpLoading(false);
    }
  }, []);

  useEffect(() => {
    runStatus();
    runSummary();
    runRetention();
    runImpersonations();
  }, [runStatus, runSummary, runRetention, runImpersonations]);

  useEffect(() => {
    if (initialAuditLoad.current) return;
    initialAuditLoad.current = true;
    runAudit();
  }, [runAudit]);

  const filterChips = useMemo(() => {
    const chips = [];
    if (filters.event_category) chips.push(`category=${filters.event_category}`);
    if (filters.action_type) chips.push(`action=${filters.action_type}`);
    if (filters.resource_type) chips.push(`resource=${filters.resource_type}`);
    if (filters.actor_email) chips.push(`actor~${filters.actor_email}`);
    if (filters.patient_id) chips.push(`patient=${filters.patient_id}`);
    if (filters.search) chips.push(`search=${filters.search}`);
    if (filters.success) chips.push(`success=${filters.success}`);
    return chips;
  }, [filters]);

  const categoryCounts = summary?.categories || {};
  const statCards = [
    { label: "Events sampled", value: summary?.total ?? rows.length, color: "blue" },
    { label: "PHI access", value: categoryCounts.PHI ?? 0, color: "cyan" },
    { label: "Denied", value: categoryCounts.DENIED ?? 0, color: "red" },
    { label: "Data sent out", value: categoryCounts.DATA_OUT ?? 0, color: "purple" },
    { label: "Active view-as", value: impRows.filter((r) => !r.ended_at).length, color: "pink" },
  ];

  return (
    <VStack align="stretch" spacing={6}>
      {writerStatus && (
        <Alert status={writerStatus.enabled ? "success" : "error"} borderRadius="md" variant="subtle">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">{writerStatus.enabled ? "Audit writer is enabled and reachable" : "Audit recording is off"}</Text>
            <Text fontSize="sm">{displayText(writerStatus.summary, "Audit status unavailable")}</Text>
            {!writerStatus.enabled && writerStatus.missing_env?.length > 0 && (
              <HStack mt={2} wrap="wrap">
                {writerStatus.missing_env.map((name) => <Tag key={name} colorScheme="red" size="sm"><TagLabel>{name}</TagLabel></Tag>)}
              </HStack>
            )}
          </Box>
        </Alert>
      )}
      {writerStatus?.table_reachable && writerStatus.migration_applied === false && (
        <Alert status="warning" borderRadius="md" variant="subtle">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Audit schema needs its compliance migration</Text>
            <Text fontSize="sm">Apply sql/migrations/2026-10-09-phi-audit-log-compliance-fields.sql in the Supabase SQL editor, then reopen this tab to refresh the schema check.</Text>
          </Box>
        </Alert>
      )}
      <Box borderWidth="1px" borderRadius="md" px={3} py={2} bg="gray.50" _dark={{ bg: "whiteAlpha.100" }}>
        <HStack spacing={4} wrap="wrap" divider={<Divider orientation="vertical" h="5" borderColor="gray.300" />}>
          {statCards.map((stat) => (
            <HStack key={stat.label} spacing={2}>
              <Text fontSize="sm" color="gray.700">{stat.label}</Text>
              <Badge colorScheme={stat.color} fontSize="sm" fontFamily="mono">
                {Number(stat.value || 0).toLocaleString()}
              </Badge>
            </HStack>
          ))}
        </HStack>
      </Box>

      <Box>
        <Text fontWeight="bold" mb={1}>Retention</Text>
        <Text fontSize="sm" color="gray.600" mb={3}>
          HIPAA section 164.316(b)(2)(i) requires 6 years minimum; this deployment locks retention at 7.
        </Text>
        <RetentionSummary status={retention} age={retentionAge ? formatAge(retentionAge) : null} />
        <HStack mt={3}>
          <Button size="sm" onClick={runRetention} isLoading={retentionLoading}>
            Refresh retention status
          </Button>
          {retentionAge && !retentionLoading && (
            <Text fontSize="sm" color="gray.600">Updated {formatAge(retentionAge)}</Text>
          )}
        </HStack>
      </Box>

      <Divider />

      <Box>
        <HStack mb={2} justify="space-between">
          <Box>
            <Text fontWeight="bold">Audit log entries (phi_audit_log)</Text>
            <Text fontSize="sm" color="gray.600">
              One row per event. Filter by family, actor, patient or path, then refresh; expand a row for full context.
            </Text>
          </Box>
          <HStack>
            <Button colorScheme="blue" onClick={() => { runAudit(); runSummary(); }} isLoading={loading} loadingText="Loading...">
              {rows.length ? "Refresh events" : "Load events"}
            </Button>
          </HStack>
        </HStack>
        <HStack spacing={2} mb={3} wrap="wrap">
          {["PHI", "AUTH", "DENIED", "ADMIN", "DATA_OUT", "IMPERSONATION"].map((category) => (
            <Button
              key={category}
              size="xs"
              colorScheme={filters.event_category === category ? "blue" : "gray"}
              variant={filters.event_category === category ? "solid" : "outline"}
              onClick={() => setFilters((f) => ({ ...f, event_category: f.event_category === category ? "" : category }))}
            >
              {({ PHI: "PHI access", AUTH: "Sign-ins", DENIED: "Blocked", ADMIN: "Admin changes", DATA_OUT: "Left system", IMPERSONATION: "View-as" })[category]} ({categoryCounts[category] || 0})
            </Button>
          ))}
        </HStack>
        <Stack direction={{ base: "column", md: "row" }} spacing={2} mb={3} align="flex-end">
          <Box>
            <Text fontSize="xs" color="gray.600">Action</Text>
            <Select
              size="sm"
              value={filters.action_type}
              onChange={(e) => setFilters((f) => ({ ...f, action_type: e.target.value }))}
              minW="180px"
            >
              {ACTION_OPTIONS.map((a) => (
                <option key={a || "any"} value={a}>{ACTION_LABELS[a] || a || "any"}</option>
              ))}
            </Select>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.600">Resource</Text>
            <Input
              size="sm"
              placeholder="e.g. notes, patients"
              value={filters.resource_type}
              onChange={(e) => setFilters((f) => ({ ...f, resource_type: e.target.value }))}
            />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.600">Actor email contains</Text>
            <Input
              size="sm"
              placeholder="user@example.com"
              value={filters.actor_email}
              onChange={(e) => setFilters((f) => ({ ...f, actor_email: e.target.value }))}
            />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.600">Patient id</Text>
            <Input size="sm" placeholder="patient id" value={filters.patient_id}
              onChange={(e) => setFilters((f) => ({ ...f, patient_id: e.target.value }))} />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.600">Search actor, patient id or API path</Text>
            <Input size="sm" placeholder="email, patient id, endpoint" value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.600">Success</Text>
            <Select
              size="sm"
              value={filters.success}
              onChange={(e) => setFilters((f) => ({ ...f, success: e.target.value }))}
              minW="120px"
            >
              <option value="">either</option>
              <option value="true">only success</option>
              <option value="false">only failures</option>
            </Select>
          </Box>
        </Stack>
        {filterChips.length > 0 && (
          <HStack mb={3} spacing={2} wrap="wrap">
            {filterChips.map((c) => (
              <Tag size="sm" key={c} colorScheme="blue"><TagLabel>{c}</TagLabel></Tag>
            ))}
            <Button size="xs" variant="link" onClick={() => setFilters({ event_category: "", action_type: "", resource_type: "", actor_email: "", patient_id: "", search: "", success: "" })}>Clear filters</Button>
          </HStack>
        )}
        {loadError && <Alert status="error" borderRadius="md"><AlertIcon /><Text fontSize="sm">{displayText(loadError)}</Text></Alert>}
        {loadWarnings.length > 0 && <Alert status="warning" borderRadius="md"><AlertIcon /><Text fontSize="sm">Category or patient filtering needs the compliance SQL migration. The current results may be unfiltered for those fields.</Text></Alert>}
        <AuditTable rows={rows} loading={loading} />
        {lastRun && !loading && (
          <Text fontSize="xs" color="gray.500" mt={2}>Last run {formatAge(lastRun)}</Text>
        )}
      </Box>

      <Divider />

      <Box>
        <HStack mb={2} justify="space-between">
          <Box>
            <Text fontWeight="bold">View-as-user sessions (impersonation_audit)</Text>
            <Text fontSize="sm" color="gray.600">
              Every view-as-user session is written to impersonation_audit and appears in the event timeline.
            </Text>
          </Box>
          <HStack>
            <Button colorScheme="pink" onClick={runImpersonations} isLoading={impLoading} loadingText="Loading...">
              {impRows.length ? "Re-query" : "Query"}
            </Button>
          </HStack>
        </HStack>
        <ActiveImpersonations rows={impRows} />
        <ImpersonationTable rows={impRows} loading={impLoading} />
        {impLastRun && !impLoading && (
          <Text fontSize="xs" color="gray.500" mt={2}>Last run {formatAge(impLastRun)}</Text>
        )}
      </Box>
    </VStack>
  );
}
